import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
  StoreLinkType,
} from '@ygoargentina/sdk/api/api.types';
import z from 'zod';
import axiosClient from './request-client';
import { AxiosRequestConfig } from 'axios';

export type EntrepreneurLink = {
  id: number;
  entrepreneurId: number;
  type: StoreLinkType;
  url: string;
  label: Nullable<string>;
  customIcon: Nullable<string>;
};

export type Entrepreneur = {
  id: number;
  name: string;
  logoUrl: Nullable<string>;
  description: Nullable<string>;
  tags: Nullable<string[]>;
  links: EntrepreneurLink[];
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
};

export const entrepreneurDetailSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export const entrepreneurLinkSchema = z.object({
  type: z.string(),
  url: z.string(),
  label: z.string().optional(),
  customIcon: z.string().optional(),
});

export const getPublicEntrepreneurs = async (
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(null).request<WithMeta<Entrepreneur>>(
    gql`
      query publicEntrepreneurs($filters: EntrepreneurFilters) {
        data: publicEntrepreneurs(filters: $filters) {
          data {
            id
            name
            logoUrl
            description
            tags

            links {
              id
              type
              url
              label
              customIcon
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    variables
  );

export const listEntrepreneurs = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Entrepreneur>>(
    gql`
      query entrepreneurs($filters: EntrepreneurFilters) {
        data: entrepreneurs(filters: $filters) {
          data {
            id
            name
            logoUrl
            description
            tags

            links {
              id
              type
              url
              label
              customIcon
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters: variables }
  );

export const getEntrepreneur = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<Entrepreneur>>(
    gql`
      query entrepreneur($id: Int!) {
        data: entrepreneur(id: $id) {
          id
          name
          logoUrl
          description
          tags

          links {
            id
            type
            url
            label
            customIcon
          }
          createdAt
          updatedAt
        }
      }
    `,
    { id }
  );

export const createEntrepreneur = async (
  user: Nullable<UserWithToken>,
  input: z.infer<typeof entrepreneurDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<Entrepreneur>>(
    gql`
      mutation createEntrepreneur($input: EntrepreneurInput!) {
        data: createEntrepreneur(input: $input) {
          id
          name
        }
      }
    `,
    { input }
  );

export const updateEntrepreneur = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: z.infer<typeof entrepreneurDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<Entrepreneur>>(
    gql`
      mutation updateEntrepreneur($id: Int!, $input: EntrepreneurInput!) {
        data: updateEntrepreneur(id: $id, input: $input) {
          id
          name
          logoUrl
          description
          tags

          links {
            id
            type
            url
            label
            customIcon
          }
        }
      }
    `,
    { id, input }
  );

export const deleteEntrepreneur = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<Entrepreneur>>(
    gql`
      mutation deleteEntrepreneur($id: Int!) {
        data: deleteEntrepreneur(id: $id) {
          id
        }
      }
    `,
    { id }
  );

export const uploadEntrepreneurLogo = (
  entrepreneurId: number,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('logo', file);

  return axiosClient.post(`/entrepreneurs/${entrepreneurId}/logo`, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteEntrepreneurLogo = (
  entrepreneurId: number,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(`/entrepreneurs/${entrepreneurId}/logo`, config);
};
